import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { buildCsv, downloadCsv } from "@/lib/csv";
import { formatDMY } from "@/lib/date";
import { Printer, Download, X } from "lucide-react";
import { savePdfA4Landscape_Aggregate } from "@/lib/pdf-aggregate";
import { aggregateFilenameStem } from "@/lib/aggregateFilename";

/**
 * Ad-hoc "aggregate scores across the selected competitions" modal.
 *
 * - Fetches once from POST /api/stats/aggregate on open, then renders top-5.
 * - Print PDF: A4 landscape leaderboard PDF.
 * - Export CSV: single-row winner (Rank, I.D., Photographer, Total) —
 *   matches the product decision that only the top scorer + top score
 *   is emitted to CSV.
 * - Nothing is persisted server-side.
 */
export function AggregateLeaderboardModal({ competitionIds, onClose, clubName }) {
  const [state, setState] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/stats/aggregate", {
          competition_ids: competitionIds,
          limit: 5,
        });
        setState({ loading: false, error: "", data });
      } catch (ex) {
        setState({
          loading: false,
          error: formatApiError(ex.response?.data?.detail) || ex.message,
          data: null,
        });
      }
    })();
  }, [competitionIds]);

  const data = state.data;
  const comps = data?.competitions || [];
  const rows = data?.leaderboard || [];

  const exportCsv = () => {
    if (!rows.length) return;
    const winner = rows[0];
    const headers = ["Rank", "I.D.", "Photographer", "Total"];
    const csv = buildCsv(headers, [
      {
        Rank: winner.rank,
        "I.D.": winner.photographer_code || "",
        Photographer: winner.photographer_name || "",
        Total: winner.total,
      },
    ]);
    const safe = `${aggregateFilenameStem(comps)}_top_scorer.csv`;
    downloadCsv(safe, csv);
  };

  const printPdf = () => {
    if (!rows.length) return;
    savePdfA4Landscape_Aggregate({
      clubName: data?.club_name || clubName || "",
      competitions: comps,
      leaderboard: rows,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
      data-testid="aggregate-modal"
    >
      <div
        className="w-full max-w-5xl max-h-[90vh] overflow-y-auto"
        style={{ background: "#11061d", border: "1px solid #3f3f3f" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ borderBottom: "1px solid #1f1f1f" }}
        >
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">
              Aggregate scores
            </div>
            <div className="text-base font-semibold tracking-tight">
              Top 5 across {competitionIds.length} competitions
            </div>
          </div>
          <button
            onClick={onClose}
            className="muted-text hover:text-white p-1"
            data-testid="aggregate-modal-close"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          {state.loading && (
            <div className="muted-text text-sm py-10 text-center">Crunching scores…</div>
          )}
          {state.error && (
            <div className="danger-text text-sm py-6" data-testid="aggregate-error">
              {state.error}
            </div>
          )}
          {!state.loading && !state.error && rows.length === 0 && (
            <div className="muted-text text-sm py-10 text-center" data-testid="aggregate-empty">
              No scored entries found across the selected competitions.
            </div>
          )}
          {!state.loading && rows.length > 0 && (
            <>
              <div className="mb-4 text-xs muted-text">
                Only each photographer&rsquo;s <b>highest score in each competition</b> counts —
                three images scored 18 / 15 / 12 in the same round would contribute 18 to that
                column, not 45. Photographers who didn&rsquo;t enter a competition contribute
                nothing to that column. Total = sum of those top scores across the selected
                competitions.
              </div>

              <div className="overflow-x-auto">
                <table className="ccs-table w-full" data-testid="aggregate-leaderboard-table">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>Rank</th>
                      <th style={{ width: 90 }}>I.D.</th>
                      <th>Photographer</th>
                      {comps.map((c) => (
                        <th
                          key={c.id}
                          style={{ width: 110, textAlign: "right" }}
                          title={`${c.competition_name} · ${formatDMY(c.competition_date)}`}
                        >
                          <div className="text-xs font-normal truncate">{c.competition_name}</div>
                          <div className="mono text-[10px] muted-text">
                            {formatDMY(c.competition_date)}
                          </div>
                        </th>
                      ))}
                      <th style={{ width: 90, textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={`${r.photographer_id || r.photographer_name}-${r.rank}`}
                          data-testid={`aggregate-row-${r.rank}`}>
                        <td className="mono text-amber-400 font-medium">#{r.rank}</td>
                        <td className="mono">{r.photographer_code || "—"}</td>
                        <td className="font-medium">{r.photographer_name || "—"}</td>
                        {comps.map((c) => {
                          const v = r.per_competition?.[c.id];
                          const t = r.per_competition_title?.[c.id];
                          return (
                            <td
                              key={c.id}
                              className="mono text-right align-top"
                              style={{ color: v == null ? "#555" : undefined }}
                            >
                              <div>{v == null ? "—" : v}</div>
                              {t && (
                                <div
                                  className="text-[10px] italic mt-0.5 truncate"
                                  style={{ color: "#8a8a8a", fontFamily: "inherit" }}
                                  title={t}
                                >
                                  {t}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td
                          className="mono text-right font-semibold"
                          style={{ color: "#fbbf24" }}
                          data-testid={`aggregate-total-${r.rank}`}
                        >
                          {r.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div
          className="px-6 py-4 flex items-center justify-end gap-2"
          style={{ borderTop: "1px solid #1f1f1f" }}
        >
          <button
            onClick={exportCsv}
            disabled={!rows.length}
            className="ccs-btn-ghost flex items-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="aggregate-export-csv"
            title="Download a 1-row CSV containing just the top scorer's Rank, I.D., Name and Total"
          >
            <Download size={14} /> Export winner CSV
          </button>
          <button
            onClick={printPdf}
            disabled={!rows.length}
            className="ccs-btn-primary flex items-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="aggregate-print-pdf"
            title="Download an A4 landscape PDF of this leaderboard"
          >
            <Printer size={14} /> Print PDF
          </button>
        </div>
      </div>
    </div>
  );
}
